import React from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Collapse,
  IconButton,
  Chip,
} from "@material-ui/core";
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircle as CheckCircleIcon,
} from "@material-ui/icons";
import { makeStyles } from "@material-ui/styles";
import FilaMovimiento from "./resultados/FilaMovimiento";

const useStyles = makeStyles((theme) => ({
  seccionContainer: {
    marginTop: theme.spacing(3),
    border: `2px solid ${theme.palette.success.main}`,
    borderRadius: theme.spacing(1),
    overflow: "hidden",
    backgroundColor: "rgba(76, 175, 80, 0.02)", // Verde muy suave
    marginBottom: theme.spacing(3),
  },
  header: {
    padding: theme.spacing(2),
    backgroundColor: "rgba(76, 175, 80, 0.1)", // Verde suave
    cursor: "pointer",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    transition: "background-color 0.2s ease",
    "&:hover": {
      backgroundColor: "rgba(76, 175, 80, 0.15)",
    },
  },
  headerLeft: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
  },
  icon: {
    color: theme.palette.success.main,
    fontSize: 28,
  },
  titulo: {
    fontWeight: 600,
    color: theme.palette.success.dark,
  },
  badge: {
    backgroundColor: theme.palette.success.main,
    color: "#fff",
    padding: theme.spacing(0.5, 1.5),
    borderRadius: theme.spacing(2),
    fontSize: 12,
    fontWeight: "bold",
  },
  content: {
    padding: theme.spacing(2),
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
  filaExito: {
    animation: "$fadeInScale 0.5s ease-out",
    backgroundColor: "rgba(76, 175, 80, 0.05)",
  },
  "@keyframes fadeInScale": {
    "0%": {
      opacity: 0,
      transform: "scale(0.95)",
    },
    "100%": {
      opacity: 1,
      transform: "scale(1)",
    },
  },
}));

/**
 * Componente para mostrar la sección de movimientos recién conciliados
 *
 * Props:
 * @param {Array} recienConciliados - Array de movimientos recién conciliados
 * @param {Boolean} expanded - Si la sección está expandida
 * @param {Function} onToggle - Handler para expandir/colapsar
 * @param {Object} pagination - Estado de paginación { page, rowsPerPage }
 * @param {Function} onChangePage - Handler para cambiar página
 * @param {Function} onChangeRowsPerPage - Handler para cambiar filas por página
 */
export default function SeccionRecienConciliados({
  recienConciliados,
  expanded,
  onToggle,
  pagination,
  onChangePage,
  onChangeRowsPerPage,
}) {
  const classes = useStyles();

  // Calcular monto total
  const montoTotal = recienConciliados.reduce(
    (sum, item) => sum + (item.movimiento?.monto || 0),
    0,
  );

  // Paginación
  const { page, rowsPerPage } = pagination;
  const paginatedItems = recienConciliados.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  return (
    <Box className={classes.seccionContainer}>
      {/* HEADER CLICKEABLE */}
      <Box className={classes.header} onClick={onToggle}>
        <Box className={classes.headerLeft}>
          <CheckCircleIcon className={classes.icon} />
          <Typography variant="h6" className={classes.titulo}>
            Recién Conciliados
          </Typography>
          <Chip
            label={recienConciliados.length}
            className={classes.badge}
            size="small"
          />
        </Box>
        <IconButton size="small">
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>

      {/* CONTENIDO COLAPSABLE */}
      <Collapse in={expanded}>
        <Box className={classes.content}>
          {recienConciliados.length === 0 ? (
            // Estado vacío
            <Box className={classes.emptyState}>
              <Typography variant="body2" color="textSecondary">
                No hay movimientos conciliados en esta sesión
              </Typography>
            </Box>
          ) : (
            <>
              {/* Info resumida */}
              <Box mb={2}>
                <Typography variant="body2" color="textSecondary">
                  Total: {recienConciliados.length} movimiento
                  {recienConciliados.length !== 1 ? "s" : ""} | Monto: $
                  {montoTotal.toLocaleString("es-CL")}
                </Typography>
              </Box>

              {/* Tabla */}
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Fecha</TableCell>
                      <TableCell>Banco</TableCell>
                      <TableCell>RUT</TableCell>
                      <TableCell>Glosa</TableCell>
                      <TableCell align="right">Monto</TableCell>
                      <TableCell>Score</TableCell>
                      <TableCell>Facturas</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedItems.map((detalle, index) => (
                      <FilaMovimiento
                        key={detalle.movimiento_id}
                        detalle={detalle}
                        showActions={false}
                        categoria="recienConciliados"
                        procesandoAccion={false}
                        isProcessing={false}
                      />
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Paginación */}
              {recienConciliados.length > 10 && (
                <TablePagination
                  component="div"
                  count={recienConciliados.length}
                  page={page}
                  onPageChange={(e, newPage) => onChangePage(newPage)}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={onChangeRowsPerPage}
                  rowsPerPageOptions={[10, 25, 50]}
                  labelRowsPerPage="Filas por página:"
                  labelDisplayedRows={({ from, to, count }) =>
                    `${from}-${to} de ${count}`
                  }
                />
              )}
            </>
          )}
        </Box>
      </Collapse>
    </Box>
  );
}
